/**
 * Timezone-safe parsing of the backend's date-only strings.
 *
 * The sales API serializes Python `date` fields (e.g. `period_month`,
 * `period_start`, `period_end`) as bare `YYYY-MM-DD` strings. Passing
 * one straight to `new Date("2026-06-01")` parses it as **UTC midnight**;
 * `toLocaleDateString`/`toLocaleString` then renders it in the browser's
 * local zone. In any negative-UTC-offset zone (all of the US) that rolls
 * the clock back a day — and for a first-of-month value, back a whole
 * month: `2026-06-01` → `2026-05-31` → "May 2026". That off-by-one is
 * why the depletions header once showed "Dec 2023 – May 2026" for data
 * that actually spanned Jan 2024 – Jun 2026.
 *
 * `parseBackendDate` splits the components and builds a **local** Date
 * (`new Date(y, mIndex, d)`), so the calendar day/month is preserved
 * regardless of the viewer's timezone. Use it for any label built from a
 * date-only API string; keep `new Date(iso)` only for full timestamps
 * that already carry a zone.
 */

/** Parse a `YYYY-MM-DD` date-only string into a local-midnight `Date`. */
export function parseBackendDate(iso: string): Date {
  const [year = 0, month = 1, day = 1] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Format a date-only string as e.g. "Jun 2026". */
function monthYearLabel(iso: string): string {
  return parseBackendDate(iso).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

/** Format a date-only string as e.g. "Aug 23" — no year. */
function monthDayLabel(iso: string): string {
  return parseBackendDate(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Label the span the depletions data actually covers.
 *
 * `start` and `end` are both first-of-month, so on their own they read
 * as whole months — "Jan 2024 – Aug 2026" implies a complete August
 * when the feed is pulled mid-month and August really stops on the
 * 23rd. That makes the newest month look like a collapse against its
 * neighbours in every chart it anchors.
 *
 * `dataThrough` is the API's day-level cutoff for the final month. When
 * present we spell it out:
 *
 *     Jan 2024 – Aug 2026 (through Aug 23)
 *
 * It is omitted only when the backend has no cutoff for that month —
 * an older upload, or the month-only Greenlight feed — in which case
 * the label degrades to the plain range rather than guessing.
 */
export function coverageRangeLabel(
  start: string,
  end: string,
  dataThrough?: string | null,
): string {
  const range = `${monthYearLabel(start)} – ${monthYearLabel(end)}`;
  return dataThrough ? `${range} (through ${monthDayLabel(dataThrough)})` : range;
}
